import { Col, Row, Card, Alert, Form, Button } from 'react-bootstrap';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import Pagination from './../../../../components/shared/Pagination';
import { LoadOrders, LoadSearchedOrders } from '../../../../features/Order/OrderSlice';
import { loaderActions } from '../../../../features/Loader/LoaderSlice';
import DownloadMultipleOrderLines from '../../../../components/shared/Files/DownloadMultipleOrderLines';
import UploadMultipleOrdersLines from '../../../../components/shared/Files/UploadMultipleOrderLines';
import { formatDate } from '../../../../Helpers/date';
import AdvanceHeadersSearch from '../../../../components/shared/AdvanceSearch/HeadersSearch';
import { VendorToCompanyID } from '../../../../components/shared/Company';
import Purchase from '../../../../components/shared/Order/Purchase';
import { usePreviousValue } from '../../../../hooks/usePreviousValue';
import { buildPurchaseOrdersFilter } from '../../../../Helpers/purchaseOrderFilters';

function PurchaseOrder() {
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentVendor } = useSelector((state) => state.vendor);
  const previousVendorValue = usePreviousValue(currentVendor.vendNo);
  const loading = useSelector((state) => state.loader.open);
  const orders = useSelector((state) => state.order.list);
  const { count, fetchingOrder } = useSelector((state) => state.order);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [filterOrders, setFilterOrders] = useState([]);
  const [isFirstLoad, setisFirstLoad] = useState(true);
  const [currentPage, setCurrentPage] = useState(() => {
    if (searchParams.get('currentPage')) {
      return Number(searchParams.get('currentPage'));
    } else {
      return 1;
    }
  });
  const [dateRange, setDateRange] = useState(() => {
    if (searchParams.get('range') && searchParams.get('range') === 'none') {
      return null;
    }

    return [new Date(new Date().setDate(new Date().getDate() - 14)), new Date()];
  });

  const [skipPaginate, setSkipPaginate] = useState(() => {
    if (searchParams.get('skipPaginate') && searchParams.get('skipPaginate') === 'true') {
      return true;
    }
    return false;
  });
  const [vendorPortalStatus, setVendorPortalStatus] = useState(() => {
    if (searchParams.get('vendorPortalStatus') !== null) {
      return searchParams.get('vendorPortalStatus');
    } else {
      return 'ALL';
    }
  });

  const minDate = new Date(new Date().setDate(new Date().getDate() - 45));
  const maxDate = new Date(new Date().setDate(new Date().getDate() + 7));

  useEffect(() => {
    const queryParams = {};
    for (const p of searchParams.keys()) {
      queryParams[p] = searchParams.get(p);
    }
    if (searchParams.get('pageSize')) {
      setPageSize(Number(searchParams.get('pageSize')));
    } else {
      setSearchParams(`?${new URLSearchParams({ ...queryParams, pageSize: 10 })}`);
    }
    if (searchParams.get('currentPage')) {
      setCurrentPage(Number(searchParams.get('currentPage')));
    } else {
      setSearchParams(`?${new URLSearchParams({ ...queryParams, currentPage })}`);
    }
  }, []);

  async function updateParams() {
    const queryParams = {};
    for (const p of searchParams.keys()) {
      queryParams[p] = searchParams.get(p);
    }
    if (dateRange !== null) {
      delete queryParams.range;
      queryParams.startDate = await formatDate(dateRange[0]);
      queryParams.endDate = await formatDate(dateRange[1]);
    } else {
      delete queryParams.startDate;
      delete queryParams.endDate;
      queryParams.range = 'none';
    }
    queryParams.skipPaginate = skipPaginate;
    queryParams.vendorPortalStatus = vendorPortalStatus;
    let page;
    if (isFirstLoad) {
      if (Number(searchParams.get('currentPage')) > currentPage) {
        page = Number(searchParams.get('currentPage'));
      } else {
        page = currentPage;
      }
    } else {
      page = currentPage;
    }
    setSearchParams(`?${new URLSearchParams({ ...queryParams, pageSize, currentPage: page })}`);
  }

  async function fetchOrdersList() {
    await dispatch(loaderActions.show());
    const filter = buildPurchaseOrdersFilter(currentVendor, { dateRange, vendorPortalStatus });
    if (currentVendor && !fetchingOrder) {
      await dispatch(
        LoadOrders({
          endpoint: '/purchase_order_header',
          companyId: VendorToCompanyID(currentVendor),
          filter,
          type: 'list',
          currentPage,
          pageSize,
          skipPaginate
        })
      );
    }
    await dispatch(loaderActions.hide());
  }

  async function loadFilteredOrders() {
    const filter = `${buildPurchaseOrdersFilter(currentVendor)} and contains(docNo,'${search}')`;

    await dispatch(loaderActions.show());
    await dispatch(
      LoadSearchedOrders({
        endpoint: '/purchase_order_header',
        companyId: VendorToCompanyID(currentVendor),
        filter
      })
    );
    await dispatch(loaderActions.hide());
  }

  function changeVendorPortalStatusFilter(v) {
    setCurrentPage(1);
    setVendorPortalStatus(v);
  }

  useEffect(() => {
    updateParams();
    fetchOrdersList();
    if (previousVendorValue !== undefined && Object.keys(currentVendor).length > 0) {
      if (previousVendorValue !== currentVendor.vendNo) {
        setCurrentPage(1);
      }
    }
    setVendorPortalStatus(vendorPortalStatus);
  }, [currentVendor, pageSize, currentPage, dateRange, skipPaginate, vendorPortalStatus]);

  const orderList = filterOrders.map((order, index) => {
    return (
      <Link
        to={`/purchase-order/${currentVendor.VendorType}/${order.docNo}${location.search}`}
        key={index}
      >
        <Card className="shadow-sm p-3">
          <Purchase order={order} vendorType={currentVendor.VendorType} />
        </Card>
      </Link>
    );
  });

  const DateErrorCard = (
    <>
      <Alert variant="warning">
        {currentVendor !== undefined ? (
          `No Purchase orders for ${currentVendor.vendName} with current date filter`
        ) : (
          <> No Purchase orders with current date filter</>
        )}
      </Alert>
    </>
  );

  const ErrorCard = (
    <>
      <Alert variant="warning">
        {currentVendor !== undefined ? (
          `No Purchase orders for ${currentVendor.vendName}`
        ) : (
          <> No Purchase orders</>
        )}
      </Alert>
    </>
  );

  const SearchErrorCard = (
    <>
      <Alert variant="warning">
        <>No Purchase order matching current search</>
      </Alert>
    </>
  );

  const selectOptions = [
    { value: 'ALL', label: 'All' },
    {
      value: 'Confirmation_x0020_Sent_x0020_to_x0020_Vendor',
      label: 'Confirmation Sent to Vendor'
    },
    { value: 'Confirmed_x0020_by_x0020_Vendor', label: 'Confirmed by Vendor' },
    {
      value: 'Confirmation_x0020_Sent_x0020_to_x0020_Whse',
      label: 'Confirmation Sent to Warehouse'
    },
    { value: 'Confirmed_x0020_by_x0020_Whse', label: 'Confirmed by Warehouse' }
  ].map((option) => {
    return (
      <option value={option.value} key={option.value}>
        {option.label}
      </option>
    );
  });

  useEffect(() => {
    const filterOrders = orders.filter((order) =>
      order.docNo.toLowerCase().includes(search.trim().toLowerCase())
    );
    setFilterOrders(filterOrders);

    if (filterOrders.length === 0 && search.trim() !== '') {
      loadFilteredOrders();
    }
  }, [search]);

  useEffect(() => {
    const filterOrders = orders.filter((order) =>
      order.docNo.toLowerCase().includes(search.trim().toLowerCase())
    );
    setFilterOrders(filterOrders);
  }, [orders]);

  return (
    <>
      <Row>
        <Col md={{ span: 3 }} sm={{ span: 6 }} xs={{ span: 12 }}>
          <Button variant="link" onClick={() => setSkipPaginate(!skipPaginate)}>
            {skipPaginate === true ? 'Apply Pagination' : 'Show All'}
          </Button>
        </Col>
        <Col md={{ span: 3 }} sm={{ span: 6 }} xs={{ span: 12 }}>
          <DateRangePicker
            minDate={minDate}
            maxDate={maxDate}
            onChange={(e) => {
              setDateRange(e);
            }}
            value={dateRange}
          />
        </Col>
        <Col md={{ span: 2 }} sm={{ span: 6 }} xs={{ span: 12 }}>
          <Form.Control
            size="sm"
            as="select"
            defaultValue={vendorPortalStatus}
            onChange={(e) => changeVendorPortalStatusFilter(e.target.value)}
          >
            {selectOptions}
          </Form.Control>
        </Col>
        <Col md={{ span: 4 }} sm={{ span: 6 }} xs={{ span: 12 }}>
          <div className="d-flex justify-content-end">
            <Form.Control
              size="sm"
              type="text"
              className="mr-2"
              placeholder="Search PO by #"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <AdvanceHeadersSearch
              query={search}
              type={'purchase'}
              vendorNo={currentVendor.vendNo}
              vendorType={currentVendor.VendorType}
              directLink="/purchase-order"
            />
            <UploadMultipleOrdersLines
              vendorNo={currentVendor.vendNo}
              vendName={currentVendor.vendName}
              vendorType={currentVendor.VendorType}
              vpFreightType={currentVendor.vpFreightType}
              directLink="/purchase-order"
              type="purchase"
            />
            {orders.length > 0 && (
              <DownloadMultipleOrderLines
                endpoint={'/purchase_order_lines'}
                vendorNo={currentVendor.vendNo}
                vendName={currentVendor.vendName}
                vendorType={currentVendor.VendorType}
                vpFreightType={currentVendor.vpFreightType}
                dateRange={dateRange !== null ? dateRange : []}
                vendorPortalStatus={vendorPortalStatus}
                type="purchase"
              />
            )}
          </div>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col xl={12} md={12}>
          {filterOrders.length > 0 ? (
            orderList
          ) : orders.length > 0 ? (
            SearchErrorCard
          ) : loading ? (
            <></>
          ) : dateRange === null ? (
            ErrorCard
          ) : (
            DateErrorCard
          )}
          {skipPaginate === false && count > pageSize ? (
            search.trim().length === 0 ? (
              <Card className="shadow-sm pt-2">
                <Card.Body>
                  <Row>
                    <Col>
                      <p>Total Orders: {count}</p>
                      <p>
                        Currently Viewing : {(currentPage - 1) * pageSize + 1} to{' '}
                        {currentPage * pageSize > count ? count : currentPage * pageSize}
                      </p>
                    </Col>
                    <Col className="text-center">
                      <Form.Group className="mt-2">
                        <Form.Control
                          as="select"
                          onChange={(e) => {
                            setCurrentPage(1);
                            setPageSize(e.target.value);
                          }}
                        >
                          <option value="10">Orders per page: 10</option>
                          <option value="20">Orders per page: 20</option>
                          <option value="25">Orders per page: 25</option>
                          <option value="50">Orders per page: 50</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col className="text-right mt-2">
                      <Pagination
                        className="pagination-bar"
                        currentPage={currentPage}
                        totalCount={count}
                        pageSize={pageSize}
                        onPageChange={(page) => {
                          setisFirstLoad(false);
                          setCurrentPage(page);
                        }}
                      />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ) : (
              <></>
            )
          ) : (
            <></>
          )}
        </Col>
      </Row>
    </>
  );
}

export default PurchaseOrder;
